import React, { useState } from 'react';
import { Button } from 'primereact/button';

interface GlobalButtonProps {
  label: string;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'outline';
  size?: 'small' | 'medium' | 'large';
  disabled?: boolean;
  loading?: boolean;
  icon?: string;
  iconPos?: 'left' | 'right';
  fullWidth?: boolean;
  rounded?: boolean;
}

export const GlobalButton: React.FC<GlobalButtonProps> = ({
  label,
  onClick,
  type = 'button',
  variant = 'primary',
  size = 'medium',
  disabled = false,
  loading = false,
  icon,
  iconPos = 'left',
  fullWidth = false,
  rounded = true,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const getVariantStyles = (): {
    background: string;
    color: string;
    boxShadow: string;
    border?: string;
  } => {
    const variants = {
      primary: {
        background: loading || disabled
          ? 'linear-gradient(135deg, #6b7ba8 0%, #4a5a8a 100%)'
          : isHovered
          ? 'linear-gradient(135deg, #1a3370 0%, #0e225d 100%)'
          : 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)',
        color: loading || disabled ? 'rgba(255,255,255,0.7)' : 'white',
        boxShadow: loading || disabled
          ? 'none'
          : isHovered
          ? '0 12px 35px rgba(14, 34, 93, 0.4)'
          : '0 8px 25px rgba(14, 34, 93, 0.3)',
      },
      secondary: {
        background: loading || disabled
          ? 'linear-gradient(135deg, #d1d5db 0%, #9ca3af 100%)'
          : isHovered
          ? 'linear-gradient(135deg, #4b5563 0%, #6b7280 100%)'
          : 'linear-gradient(135deg, #6b7280 0%, #9ca3af 100%)',
        color: 'white',
        boxShadow: loading || disabled
          ? 'none'
          : isHovered
          ? '0 12px 35px rgba(107, 114, 128, 0.4)'
          : '0 8px 25px rgba(107, 114, 128, 0.3)',
      },
      success: {
        background: loading || disabled
          ? 'linear-gradient(135deg, #a9d3b8 0%, #7ab894 100%)'
          : isHovered
          ? 'linear-gradient(135deg, #0f5c31 0%, #15803d 100%)'
          : 'linear-gradient(135deg, #15803d 0%, #1f9d4d 100%)',
        color: 'white',
        boxShadow: loading || disabled
          ? 'none'
          : isHovered
          ? '0 12px 35px rgba(21, 128, 61, 0.4)'
          : '0 8px 25px rgba(21, 128, 61, 0.3)',
      },
      danger: {
        background: loading || disabled
          ? 'linear-gradient(135deg, #d99a9a 0%, #c47474 100%)'
          : isHovered
          ? 'linear-gradient(135deg, #8f2222 0%, #b02a2a 100%)'
          : 'linear-gradient(135deg, #b02a2a 0%, #8f2222 100%)',
        color: 'white',
        boxShadow: loading || disabled
          ? 'none'
          : isHovered
          ? '0 12px 35px rgba(176, 42, 42, 0.4)'
          : '0 8px 25px rgba(176, 42, 42, 0.3)',
      },
      warning: {
        background: loading || disabled
          ? 'linear-gradient(135deg, #f5d78a 0%, #e0a800 100%)'
          : isHovered
          ? 'linear-gradient(135deg, #e09a00 0%, #fbb21f 100%)'
          : 'linear-gradient(135deg, #fbb21f 0%, #e09a00 100%)',
        color: '#0e225d',
        boxShadow: loading || disabled
          ? 'none'
          : isHovered
          ? '0 12px 35px rgba(251, 178, 31, 0.4)'
          : '0 8px 25px rgba(251, 178, 31, 0.3)',
      },
      outline: {
        background: isHovered ? 'rgba(14, 34, 93, 0.05)' : 'transparent',
        color: disabled ? '#9ca3af' : '#0e225d',
        border: `1px solid ${disabled ? '#e5e7eb' : '#0e225d'}`,
        boxShadow: 'none',
      },
    };
    return variants[variant];
  };

  const getSizeStyles = () => {
    const sizes = {
      small: {
        padding: '8px 24px',
        fontSize: '0.8rem',
      },
      medium: {
        padding: '12px 32px',
        fontSize: '0.9rem',
      },
      large: {
        padding: '14px 40px',
        fontSize: '1rem',
      },
    };
    return sizes[size];
  };

  const variantStyles = getVariantStyles();
  const sizeStyles = getSizeStyles();

  return (
    <Button
      label={label}
      onClick={onClick}
      type={type}
      disabled={disabled || loading}
      loading={loading}
      icon={icon}
      iconPos={iconPos}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        ...variantStyles,
        ...sizeStyles,
        width: fullWidth ? '100%' : 'auto',
        border: variantStyles.border || 'none',
        borderRadius: rounded ? '12px' : '6px',
        fontWeight: 600,
        cursor: (disabled || loading) ? 'not-allowed' : 'pointer',
        transform: (loading || disabled || !isHovered) ? 'translateY(0)' : 'translateY(-2px)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        opacity: disabled ? 0.6 : 1,
      }}
      className="global-button"
    />
  );
};